import {
    Component,
    Input,
    ChangeDetectionStrategy,
    OnInit,
    ChangeDetectorRef,
    signal,
    ViewEncapsulation,
    ElementRef,
    ViewChild,
    Output,
    EventEmitter,
    input,
    effect,
} from "@angular/core";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Item } from "../../../pojo/Item";
import { ContactRole, UpdatedContactRoles, ContactRoles, ContactRoleId } from "../../../services/organization.service";
import { S25ItemComponent } from "../../s25-item/s25.item.component";
import { Table } from "../../s25-table/Table";
import Unpaginated = Table.Unpaginated;
import Column = Table.Column;
import { S25RadioComponent } from "../../s25-radio/s25.radio.component";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { Bind } from "../../../decorators/bind.decorator";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { S25Util } from "../../../util/s25-util";
import { ObjectDetailsUtil } from "../../../util/object.details.util";
import { S25WsMicroService } from "../../../services/MicroServices/S25WsMicroService";
import { S25WsMicroOrganization } from "../../../services/MicroServices/S25WsMicroOrganization";
import { S25MultiselectSearchCriteriaComponent } from "../../s25-multiselect/multiselects/s25.multiselect.search.criteria.component";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";

@TypeManagerDecorator("s25-ng-org-contact-roles")
@Component({
    selector: "s25-ng-org-contact-roles",
    template: `
        @if (dataReady()) {
            <s25-ng-table
                class="c-margin-top--half org-roles-table"
                [dataSource]="tableConfig"
                [columnSortable]="false"
                [noResultsMessage]="'No Associated Contacts'"
                [pivotThreshold]="300"
            >
            </s25-ng-table>
        }

        @if (message()) {
            <div class="ngRed">{{ message() }}</div>
        }
        @if (canEdit()) {
            <s25-ng-dropdown-search-criteria
                class="c-margin-bottom--half c-margin-top--single org-roles-contacts"
                [type]="'contacts'"
                [placeholder]="'Add a Contact'"
                (valueChange)="onAdd($event)"
            />
        }
    `,
    styles: `
        .org-roles-contacts {
            display: block;
            padding-inline: 1rem;
        }

        :host ::ng-deep s25-ng-table thead {
            background-color: var(--base-background) !important;
        }

        :host ::ng-deep s25-ng-table thead th .headerText {
            font-weight: bold;
        }

        :host ::ng-deep .s25-item--contact {
            .s25-item-holder {
                display: flex;
                align-items: flex-start;
                flex-wrap: wrap;
                position: relative;
                top: -15px;
            }
        }

        :host ::ng-deep .org-roles-table table {
            table-layout: fixed;
        }

        :host ::ng-deep .org-roles-table thead tr th[col="primary"] {
            width: 18%;
        }

        :host ::ng-deep .org-roles-table thead tr th[col="contact"] {
            width: 35%;
        }

        :host ::ng-deep .org-roles-table thead tr th[col="role"] {
            width: 57%;
        }

        :host ::ng-deep .org-roles-table thead tr th[col="remove"] {
            width: 9%;
        }

        :host ::ng-deep s25-ng-table td {
            vertical-align: top !important;
        }

        :host ::ng-deep s25-ng-dropdown-search-criteria {
            padding-bottom: 1rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [S25TableModule, S25DropdownPaginatedModule],
})
export class S25OrgContactRolesComponent {
    @Input() itemId: number; //OrganizationId
    @Input() contacts: ContactRole[] = [];
    canEdit = input<boolean>(false);
    @Input() onChange: (
        primaryId: number | undefined,
        changed: UpdatedContactRoles,
    ) => Promise<S25WsMicroService.Content<S25WsMicroOrganization.Content>>;
    @Output() changes = new EventEmitter<UpdatedContactRoles>();

    @ViewChild(S25TableComponent) table: S25TableComponent;

    primary: number | undefined;
    tableConfig: Unpaginated;
    changed: UpdatedContactRoles = { added: [], removed: [], updated: [] };
    message = signal<string>("");
    dataReady = signal<boolean>(false);

    contactRoles: ContactRoles[] = [];
    initContactRoles: ContactRoles[] = [];

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
        effect(() => {
            this.dataReady.set(false);
            this.cd.detectChanges();
            this.init();
        });
    }

    /**
     * Configure the table columns based on whether the user can edit or not.
     */
    configureTable() {
        let columns: Column[] = [];

        if (this.canEdit()) {
            columns = [
                {
                    header: "Primary",
                    id: "primary",
                    sortable: true,
                    align: "center",
                    cells: {
                        component: S25RadioComponent,
                        outputs: { valueChange: this.setPrimary },
                    },
                },
                {
                    header: "Contact",
                    id: "contact",
                    sortable: true,
                    align: "left",
                    cells: {
                        component: S25ItemComponent,
                    },
                },
                {
                    header: "Role",
                    id: "role",
                    sortable: true,
                    align: "center",
                    cells: {
                        component: S25MultiselectSearchCriteriaComponent,
                        outputs: { onDone: this.changeRole },
                    },
                },
                {
                    header: "",
                    id: "remove",
                    align: "center",
                    cells: {
                        component: S25IconButtonComponent,
                        inputs: {
                            type: "close",
                            ariaLabel: "Remove",
                        },
                        outputs: { clicked: this.onRemove },
                    } satisfies Table.Cell<S25IconButtonComponent>,
                },
            ];
        } else {
            columns = [
                {
                    header: "Contact",
                    id: "contact",
                    sortable: true,
                    cells: {
                        component: S25ItemComponent,
                    },
                    align: "left",
                },
                { id: "role", header: "Role", align: "center" },
                {
                    header: "Primary",
                    id: "primary",
                    sortable: true,
                    align: "center",
                },
            ];
        }

        this.tableConfig = {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: columns,
        };
    }

    getRows = () => {
        const rows: Table.Row[] = this.contactRoles?.map(this.mapToRow) ?? [];
        return Promise.resolve({ rows: rows });
    };

    private dedupeRolesById(roles: ContactRoles["roles"]): ContactRoles["roles"] {
        const roleIdToRole = new Map<number, ContactRoles["roles"][number]>();
        for (const role of roles ?? []) {
            if (role?.itemId == null) {
                continue;
            }
            const roleId = Number(role.itemId);
            if (!roleIdToRole.has(roleId)) {
                roleIdToRole.set(roleId, role);
            }
        }
        return [...roleIdToRole.values()];
    }

    @Bind
    mapToRow(contact: ContactRoles): Table.Row {
        const displayRoles = ObjectDetailsUtil.organizationRolesForReadOnlyDisplay(contact.roles);
        const editSelectedRoles = ObjectDetailsUtil.organizationRolesForMultiselectSelection(contact.roles);
        const retVal: Table.Row = {
            id: contact.itemId,
            name: contact.itemName,
            data: contact,
            cells: {},
        };

        if (this.canEdit()) {
            retVal.cells = {
                primary: { inputs: { value: this.primary, radioValue: contact.itemId, name: "primaryContact" } },
                contact: {
                    className: "s25-item-name",
                    inputs: {
                        itemId: contact.itemId,
                        itemName: contact.itemName,
                        itemTypeId: Item.Ids.Contact,
                        includeTypeIcon: true,
                    },
                },
                role: {
                    inputs: {
                        type: "organizationRoles",
                        selectedItems: editSelectedRoles,
                        showResults: true,
                        popoverOnBody: true,
                    },
                },
                remove: {
                    className: "s25-remove-button",
                    inputs: { type: "close", ariaLabel: "Remove" },
                },
            };
        } else {
            retVal.cells = {
                contact: {
                    className: "s25-item-name",
                    inputs: {
                        itemId: contact.itemId,
                        itemName: contact.itemName,
                        itemTypeId: Item.Ids.Contact,
                        includeTypeIcon: true,
                    },
                },
                role: { text: displayRoles.map((role) => role.itemName).join(", ") },
                primary: { text: contact.isPrimary ? "Yes" : "No" },
            };
        }

        return retVal;
    }

    async init(): Promise<void> {
        this.primary = this.contacts?.find((c) => c.isPrimary)?.itemId || undefined;
        this.configureTable();
        await this.mappingContactRoles(); // mapping contacts roles
        this.dataReady.set(true);
        this.cd.detectChanges();
    }

    @Bind
    onAdd(newItem: DropDownItem) {
        const contactRole = <ContactRole>{ ...newItem, role: { itemId: ContactRoleId.None, itemName: "No Role" } };
        // Check if the item already exists in the rows
        const existingIndex = this.table.rows.findIndex((row) => row.data.itemId === contactRole.itemId);
        if (existingIndex > -1) {
            this.message.set("Contact already exists in the list.");
            return;
        }

        if (!this.primary) contactRole.isPrimary = true;

        const newRow = this.mapToRow({ ...contactRole, roles: [contactRole.role] });

        this.table.addRow(newRow);
        this.changed.added.push(newRow.data);
        this.outputChanges();
    }

    @Bind
    onRemove(item: any, context: Table.CellCallbackContext) {
        this.table.removeRow(context.row);

        //remove previous changes
        this.changed.added = this.changed.added.filter((added) => added.itemId !== item.data.itemId);
        this.changed.updated = this.changed.updated.filter((updated) => updated.itemId !== item.data.itemId);

        this.changed.removed.push(context.row.data);
        this.outputChanges();
    }

    @Bind
    changeRole(items: S25ItemI[], context: Table.CellCallbackContext) {
        const currentContact = context.row.data;
        // Get the initial roles for this contact
        const initContact = this.initContactRoles?.find((c) => c.itemId === currentContact.itemId);
        const initRoles = initContact?.roles || [];
        // Create sets for comparison
        const initRoleIds = new Set(initRoles.map((r) => Number(r.itemId)));
        const newRoleIds = new Set(items.map((i) => Number(i.itemId)));
        // Find removed roles (in init but not in new)
        const removedRoles = initRoles.filter((r) => !newRoleIds.has(Number(r.itemId)));
        // Find added roles (in new but not in init)
        const addedRoles = items.filter((i) => !initRoleIds.has(Number(i.itemId)));

        // Process removed roles
        for (const removedRole of removedRoles) {
            this.changed.removed.push({
                ...currentContact,
                role: { itemId: Number(removedRole.itemId), itemName: removedRole.itemName },
            });
        }
        // Process added roles
        for (const addedRole of addedRoles) {
            this.changed.updated.push({
                ...currentContact,
                role: { itemId: Number(addedRole.itemId), itemName: addedRole.itemName },
            });
        }
        this.outputChanges();
    }

    @Bind
    async setPrimary(contId: number) {
        //for consistency update the primary contact on the original data
        const oldPrimary = this.table.rows.find((row) => row.data.isPrimary);
        const newPrimary = this.table.rows.find((row) => row.data.itemId === contId);
        if (oldPrimary) oldPrimary.data.isPrimary = false;
        if (newPrimary?.data) {
            newPrimary.data.isPrimary = true;
        }

        this.outputChanges(contId);
    }

    async outputChanges(primaryId?: number) {
        this.message.set("");
        //make sure there are no duplicates between this.changed objects
        //Added takes priority over changed and removed
        this.changed.removed = this.changed.removed.filter(
            (removed) => !this.changed.added.some((added) => added.itemId === removed.itemId),
        );
        this.changed.updated = this.changed.updated.filter(
            (updated) => !this.changed.added.some((added) => added.itemId === updated.itemId),
        );

        this.changed.primaryId = primaryId;

        const [resp, err] = await S25Util.Maybe(this.onChange && this.onChange(primaryId, this.changed));
        if (err) {
            this.message.set(err);
        }
        this.changes.emit(this.changed);
    }

    async mappingContactRoles(): Promise<void> {
        const contactsMap: { [key: number]: ContactRoles } = {};
        for (const c of this.contacts) {
            if (!contactsMap[c.itemId]) {
                contactsMap[c.itemId] = {
                    itemName: c.itemName,
                    itemId: c.itemId,
                    itemEmail: c.itemEmail,
                    itemTypeId: c.itemTypeId,
                    isPrimary: c.isPrimary,
                    roles: [],
                };
            }
            if (c.isPrimary) {
                contactsMap[c.itemId].isPrimary = true;
            }
            if (c.role?.itemId != null) {
                contactsMap[c.itemId].roles.push(c.role);
            }
        }
        for (const contact of Object.values(contactsMap)) {
            contact.roles = this.dedupeRolesById(contact.roles);
        }

        this.contactRoles = Object.values(contactsMap);
        this.initContactRoles = structuredClone(Object.values(contactsMap));
    }
}
